import { CdkScrollable } from '@angular/cdk/scrolling';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
    MAT_DIALOG_DATA,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { Store, select } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormGroup, TypedFormBuilder } from 'core/forms';
import { Session } from 'core/session.service';
import { DateTime } from 'luxon';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { AppState, playersSelectors, sidebarActions } from 'store';
import { ChipComponent } from '../chip/chip.component';

import { MatButton } from '@angular/material/button';
import { GameMode, TeamMemberRole } from 'models/enums';
import { MatchInvitationCreateModel, MatchInvitationModel, MatchInvitationUpdateModel } from 'models/match-invitations';
import { PlayerModel } from 'models/player';

interface IPlayerExt extends PlayerModel {
    selected: boolean;
    disabled: boolean;
}

export interface ISendInvitationModalContext {
    matchInvitation?: MatchInvitationModel;
    gameMode?: GameMode;
}
export interface ISendInvitationModalResult {
    message: string;
    players: Array<number>;
}

@Component({
    selector: 'app-send-invitation',
    templateUrl: './send-invitation-modal.component.html',
    styleUrls: ['./send-invitation-modal.component.scss'],
    imports: [
        MatDialogTitle,
        CdkScrollable,
        MatDialogContent,
        MatFormField,
        MatLabel,
        MatInput,
        FormsModule,
        ReactiveFormsModule,
        MatSlider,
        MatSliderThumb,
        ChipComponent,
        MatDialogActions,
        MatButton,
        MatDialogClose,
        TranslateModule,
    ],
})
export class SendInvitationModalComponent implements OnInit, OnDestroy {
    private readonly context = inject<ISendInvitationModalContext>(MAT_DIALOG_DATA);
    private readonly dialogRef = inject<MatDialogRef<SendInvitationModalComponent, ISendInvitationModalResult>>(MatDialogRef);
    private readonly translate = inject(TranslateService);
    private readonly session = inject(Session);
    private readonly store = inject<Store<AppState>>(Store);
    private readonly http = inject(HttpClient);

    #unsubscribe = new Subject<void>();

    public form: FormGroup<MatchInvitationCreateModel>;
    public players: Array<IPlayerExt>;

    constructor() {
        const context = this.context;
        const formBuilder = inject(TypedFormBuilder);

        this.form = formBuilder.group<MatchInvitationCreateModel>({
            message: [this.translate.instant('ADD_MATCH.DEFAULT_MESSAGE', { name: this.session.user.displayName }), Validators.required],
            maxOpenDuration: [60, [Validators.min(1), Validators.max(120)]],
        });

        if (context.matchInvitation) {
            const createdAt = DateTime.fromISO(context.matchInvitation.createdAt);
            const endsAt = DateTime.fromISO(context.matchInvitation.endsAt);
            const maxOpenDuration = Math.floor(endsAt.diff(createdAt, 'minutes').minutes);
            this.form.get('maxOpenDuration').setValue(maxOpenDuration);
        }
    }

    ngOnInit() {
        this.store
            .pipe(
                select(playersSelectors.selectPlayeblePlayers),
                map((res) =>
                    res.filter(
                        (x) =>
                            x.id !== this.session.user.idPlayer &&
                            (x.teamMemberRole === TeamMemberRole.Player || x.teamMemberRole === TeamMemberRole.Guest),
                    ),
                ),
                takeUntil(this.#unsubscribe),
            )
            .subscribe((players) => {
                this.players = players
                    .filter((x) => x.user.email)
                    .map((x) => {
                        const selected = this.context.matchInvitation
                            ? this.context.matchInvitation.players.some((p) => p.idPlayer === x.id)
                            : false;
                        return {
                            ...x,
                            selected: selected,
                            disabled: selected,
                        };
                    });
            });
    }

    ngOnDestroy(): void {
        this.#unsubscribe.next();
        this.#unsubscribe.complete();
    }

    public allSelected() {
        return this.players.every((x) => x.selected);
    }

    public selectAllChanged() {
        if (this.allSelected()) {
            this.players.forEach((x) => (x.selected = false));
        } else {
            this.players.forEach((x) => (x.selected = true));
        }
    }

    public saveDisabled() {
        if (this.context.gameMode === GameMode.OneAgainstOne) {
            return this.players.filter((x) => x.selected).length < 1;
        } else {
            return this.players.filter((x) => x.selected).length < 3;
        }
    }

    onSave() {
        if (this.saveDisabled() || this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const selectedPlayers = this.players.filter((x) => x.selected).map((x) => x.id);

        if (this.context.matchInvitation) {
            const model: MatchInvitationUpdateModel = {
                ...this.form.value,
                state: this.context.matchInvitation.state,
                newPlayers: selectedPlayers.filter((x) => !this.context.matchInvitation.players.some((p) => p.idPlayer === x)),
            };
            this.http.patch<never>(`/api/match-invitations/${this.context.matchInvitation.id}`, model).subscribe(() => {
                this.closeDialog();
            });
        } else {
            const model: MatchInvitationCreateModel = {
                ...this.form.value,
                players: selectedPlayers,
                gameMode: this.context.gameMode || GameMode.TwoAgainstTwo,
            };

            if (this.form.get('message').untouched) {
                model.message = null;
            }

            this.http.post<MatchInvitationModel>('/api/match-invitations', model).subscribe(() => {
                this.closeDialog();
            });
        }
    }

    private closeDialog() {
        this.store.dispatch(sidebarActions.getNotificationCount());
        this.store.dispatch(sidebarActions.openSidebar());
        this.dialogRef.close();
    }
}
